import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { createCertificado, getCertificados, updateCertificado, getPdfUrl, descargarCertificadoPDF, deleteCertificado } from '../../api/authcertificados';
import { useAuth } from '../../context/AuthContext';

function Certificado() {
	const { token } = useAuth();
	const [showForm, setShowForm] = useState(false);
	// 1. AÑADIR nom_identifier AL ESTADO INICIAL
	const [form, setForm] = useState({ marca: '', modelo: '', creada: '', vence: '', archivo: null, observaciones: '', nom_identifier: '' });
	const [filtro, setFiltro] = useState('TODOS');
	const [query, setQuery] = useState('');
	const [page, setPage] = useState(1);
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [certificadoAEliminar, setCertificadoAEliminar] = useState(null);
	const [passwordConfirmacion, setPasswordConfirmacion] = useState('');
	const [loadingDelete, setLoadingDelete] = useState(false);
	const pageSize = 10;
	const [certificados, setCertificados] = useState([]);
	const [ultimaActualizacion, setUltimaActualizacion] = useState(new Date());
	const API_BASE =
		(process.env.REACT_APP_API_URL &&
			process.env.REACT_APP_API_URL.replace(/\/+$/, '')) ||
		(window.location.hostname === 'localhost'
			? 'http://localhost:3000'
			: window.location.origin);
	// Estados para edición inline
	const [editandoCelda, setEditandoCelda] = useState(null);
	const [valorTemporal, setValorTemporal] = useState('');
	const [archivoTemporal, setArchivoTemporal] = useState(null);


	// funcion para obtener la fecha de hoy en formato YYYY-MM-DD
	const obtenerFechaHoy = () => {
		const hoy = new Date();
		return hoy.toISOString().split('T')[0]; // Formato YYYY-MM-DD
	};

	// funcion para calcular la fecha de vencimiento 365 dias despues de la fecha de creada
	const calcularFechaVencimiento = (fechaCreada) => {
		const fecha = new Date(fechaCreada);
		fecha.setFullYear(fecha.getFullYear() + 1);
		return fecha.toISOString().split('T')[0]; // Formato YYYY-MM-DD
	};

	// Función mejorada para calcular días restantes y estatus
	const calcularEstatusYDias = (fechaCreada, fechaVence) => {
		// Validar que las fechas existan
		if (!fechaVence) {
			return { dias: null, estatus: 'SIN FECHA' };
		}

		// Convertir las fechas a objetos Date
		const fechaVenceDate = new Date(fechaVence);
		const hoy = new Date();
		
		// Resetear horas para comparar solo fechas
		hoy.setHours(0, 0, 0, 0);
		fechaVenceDate.setHours(0, 0, 0, 0);
		
		// Calcular diferencia en milisegundos y convertir a días
		const diferenciaMilisegundos = fechaVenceDate.getTime() - hoy.getTime();
		const dias = Math.ceil(diferenciaMilisegundos / (1000 * 60 * 60 * 24));
		
		let estatus;
		
		// Determinar estatus basado en días restantes
		if (dias < 0) {
			estatus = 'VENCIDO';
		} else if (dias <= 30) {
			estatus = 'POR VENCER';
		} else {
			estatus = 'VIGENTE';
		}
		
		return { dias, estatus };
	};

	// Función para procesar certificados y agregar campos calculados
	const procesarCertificados = useCallback((certificadosRaw) => {
		return certificadosRaw.map(cert => {
			// Usar la nueva función de cálculo
			const { dias, estatus } = calcularEstatusYDias(cert.creada, cert.vence);
			
			return {
				...cert,
				creada: new Date(cert.creada).toLocaleDateString('es-ES'),
				vence: new Date(cert.vence).toLocaleDateString('es-ES'),
				dias: dias,
				estatus: estatus,
				// Asumo que el campo de archivo en DB es 'archivo_nombre' (como en certificados.routes.js) 
				// y aquí lo mapeas a 'numNom_img' o 'archivo' para la UI/API. Mantendré el nombre original del backend: 'archivo' para simplicidad
				pdf: cert.archivo ? true : false,
				nom: cert.nom_identifier ? true : false, // Usar nom_identifier
			};
		});
	}, []);

	// Función para actualización manual (revisada para usar la nueva lógica)
	const actualizarListaCertificados = async () => {
		try {
			const certificadosData = await getCertificados();
			const certificadosProcesados = procesarCertificados(certificadosData);
			setCertificados(certificadosProcesados);
			setUltimaActualizacion(new Date());
		} catch (error) {
			console.error('Error al actualizar certificados:', error.message);
		}
	};


	useEffect(() => {
		async function fetchCertificados() {
			try {
				const certificadosData = await getCertificados();
				const certificadosProcesados = procesarCertificados(certificadosData);
				setCertificados(certificadosProcesados);
				setUltimaActualizacion(new Date()); // Actualizar timestamp
			} catch (error) {
				console.error('Error al obtener certificados:', error.message);
			}
		}

		// Cargar datos inicialmente
		fetchCertificados();

		// Configurar actualización automática cada 1 minuto (para testing)
		const intervalo = setInterval(() => {
			console.log('🔄 Actualizando cálculos de certificados...');
			fetchCertificados();
		}, 60000); // 1 minuto

		// Limpiar intervalo al desmontar componente
		return () => {
			clearInterval(intervalo);
		};
	}, [procesarCertificados]);

	// Efecto para manejar tecla Escape en el modal
	useEffect(() => {
	const handleEscape = (event) => {
		if (event.key === 'Escape' && showForm) {
			setShowForm(false);
		}
	};

	if (showForm) {
		// Establecer fechas automáticamente cuando se abre el formulario
		const fechaHoy = obtenerFechaHoy();
		const fechaVencimiento = calcularFechaVencimiento(fechaHoy);
		
		setForm(prev => ({
			...prev,
			creada: fechaHoy,
			vence: fechaVencimiento
		}));

		document.addEventListener('keydown', handleEscape);
		// Prevenir scroll del fondo cuando el modal está abierto
		document.body.style.overflow = 'hidden';
	} else {
		document.body.style.overflow = 'unset';
	}

	return () => {
		document.removeEventListener('keydown', handleEscape);
		document.body.style.overflow = 'unset';
	};
}, [showForm]);

	const handleChange = (e) => {
		const { name, value, files } = e.target;
		
		if (name === 'creada') {
			// Si cambia la fecha de emisión, recalcular automáticamente la fecha de vencimiento
			const nuevaFechaVencimiento = calcularFechaVencimiento(value);
			setForm({ 
				...form, 
				[name]: value,
				vence: nuevaFechaVencimiento
			});
		} else {
			// Si se cambia la fecha de creación, recalcular la fecha de vencimiento
			if (name === 'creada') {
				const nuevaFechaVence = calcularFechaVencimiento(value);
				setForm({ ...form, [name]: value, vence: nuevaFechaVence });
			} else {
				setForm({ ...form, [name]: files ? files[0] : value });
			}
		}
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		try {
			// 2. INCLUIR nom_identifier EN LA LLAMADA
			const data = {
				marca: form.marca,
				modelo: form.modelo,
				creada: form.creada,
				vence: form.vence,
				observaciones: form.observaciones,
				nom_identifier: form.nom_identifier, // <-- NUEVO CAMPO A ENVIAR
			};

			await createCertificado(data, form.archivo);
			alert('Certificado creado exitosamente');
			setShowForm(false);
			setForm({ marca: '', modelo: '', creada: '', vence: '', archivo: null, observaciones: '', nom_identifier: '' }); // Resetear nom_identifier
			
			// Recargar la lista de certificados
			await actualizarListaCertificados();
		} catch (error) {
			console.error('Error al crear certificado:', error.message);
			alert(`Error al crear certificado: ${error.message}`);
		}
	};

	// Función para eliminar certificado (abrir modal)
	const abrirModalEliminar = (certificadoId, marca, modelo) => {
		setCertificadoAEliminar({ id: certificadoId, marca, modelo });
		setPasswordConfirmacion('');
		setShowDeleteModal(true);
	};

	const cerrarModalEliminar = () => {
		setShowDeleteModal(false);
		setCertificadoAEliminar(null);
		setPasswordConfirmacion('');
		setLoadingDelete(false);
	};

	const confirmarEliminacion = async () => {
		if (!passwordConfirmacion.trim()) {
			alert('Por favor ingresa tu contraseña para confirmar');
			return;
		}

		if (!certificadoAEliminar) {
			alert('Error: No se ha seleccionado un certificado');
			return;
		}

		setLoadingDelete(true);

		try {
			// ⚠️ Nota: Esta lógica de verificar contraseña en el frontend puede ser insegura 
			// si no se tiene un endpoint de verificación robusto.
			const verifyResponse = await fetch(`${API_BASE}/api/users/verify-password`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					...(token ? { Authorization: `Bearer ${token}`, 'x-access-token': token } : {}),
				},
				credentials: 'include',
				body: JSON.stringify({ password: passwordConfirmacion }),
			});

			const ct = verifyResponse.headers.get('content-type') || '';
			let verifyData = null;
			if (ct.includes('application/json')) {
				verifyData = await verifyResponse.json();
			} else {
				const raw = await verifyResponse.text();
				throw new Error(`Error verificando contraseña (${verifyResponse.status}): ${raw.slice(0, 200)}`);
			}

			if (!verifyResponse.ok || !(verifyData.valida || verifyData.valid)) {
				alert(verifyData?.mensaje || 'Contraseña incorrecta. No se puede eliminar el certificado.');
				setLoadingDelete(false);
				return;
			}
			
			// Si la contraseña es correcta, proceder con la eliminación
			await deleteCertificado(certificadoAEliminar.id);
			
			// Actualizar la lista localmente
			setCertificados(prev => prev.filter(cert => cert.id !== certificadoAEliminar.id));
			setUltimaActualizacion(new Date());
			
			cerrarModalEliminar();
			alert(`Certificado de ${certificadoAEliminar.marca} ${certificadoAEliminar.modelo} eliminado correctamente`);
			
		} catch (error) {
			console.error('❌ Error al eliminar certificado:', error);
			alert(`Error al eliminar el certificado: ${error.message}`);
			setLoadingDelete(false);
		}
	};

	// Funciones para edición inline
	const iniciarEdicion = (certificadoId, campo, valorActual) => {
		// Convertir fecha para input si es necesario
		let valor = valorActual;
		
		if (campo === 'creada' || campo === 'vence') {
			// Convertir DD/MM/YYYY a YYYY-MM-DD
			const partes = valorActual.split('/');
			if (partes.length === 3) {
				const [dia, mes, año] = partes;
				valor = `${año}-${mes.padStart(2, '0')}-${dia.padStart(2, '0')}`;
			}
		} else if (campo === 'observaciones' || campo === 'nom_identifier') {
			// Para observaciones y NOM, manejar valores nulos o undefined
			valor = valorActual === '—' || !valorActual ? '' : valorActual;
		}
		
		setEditandoCelda({ id: certificadoId, campo });
		setValorTemporal(valor);
		setArchivoTemporal(null); // Reset archivo
	};

	const cancelarEdicion = () => {
		setEditandoCelda(null);
		setValorTemporal('');
		setArchivoTemporal(null);
	};

	const guardarEdicion = async () => {
		if (!editandoCelda) return;

		try {
			const { id, campo } = editandoCelda;
			
			if (campo === 'archivo') {
				if (!archivoTemporal) {
					alert('Por favor selecciona un archivo PDF');
					return;
				}
				
				await updateCertificado(id, {}, archivoTemporal);
				await actualizarListaCertificados();
			} else {
				// Para otros campos
				const datos = { [campo]: valorTemporal };
				
				await updateCertificado(id, datos, null);
				
				// Actualizar el estado local inmediatamente
				setCertificados(prev => 
					prev.map(cert => {
						if (cert.id === id) {
							let valorMostrar = valorTemporal;
							
							// Si es fecha, convertir de YYYY-MM-DD a DD/MM/YYYY para mostrar
							if (campo === 'creada' || campo === 'vence') {
								const fecha = new Date(valorTemporal);
								valorMostrar = fecha.toLocaleDateString('es-ES');
							}
							
							// Recalcular días y estatus si cambió alguna fecha
							if (campo === 'creada' || campo === 'vence') {
								const fechaCreada = campo === 'creada' ? valorTemporal : cert.creada;
								const fechaVence = campo === 'vence' ? valorTemporal : cert.vence;
								const { dias, estatus } = calcularEstatusYDias(fechaCreada, fechaVence);
								
								return {
									...cert,
									[campo]: valorMostrar,
									dias,
									estatus
								};
							}
							
							return { ...cert, [campo]: valorMostrar };
						}
						return cert;
					})
				);
			}
			
			cancelarEdicion();
			setUltimaActualizacion(new Date());
			
		} catch (error) {
			console.error('❌ Error al guardar edición:', error);
			alert(`Error al guardar: ${error.message}`);
		}
	};

	const manejarTecla = (event) => {
		if (event.key === 'Enter' && editandoCelda.campo !== 'observaciones') {
			guardarEdicion();
		} else if (event.key === 'Escape') {
			cancelarEdicion();
		}
	};

	// Componente para celdas editables
	const CeldaEditable = ({ certificado, campo, valor, tipo = 'text', className = '' }) => {
		const estaEditando = editandoCelda?.id === certificado.id && editandoCelda?.campo === campo;
		
		if (estaEditando) {
			if (campo === 'archivo') {
				// Para editar archivo PDF
				return (
					<div className="flex flex-col gap-2 p-2 border rounded bg-white min-w-[250px]">
						<div className="text-xs text-gray-600">Archivo actual: {valor}</div>
						<input
							type="file"
							accept=".pdf"
							onChange={(e) => {
								setArchivoTemporal(e.target.files[0]);
							}}
							className="text-sm border rounded p-1"
							autoFocus
						/>
						<div className="flex gap-2 justify-center">
							<button
								onClick={guardarEdicion}
								disabled={!archivoTemporal}
								className={`px-3 py-1 rounded text-xs font-medium ${
									archivoTemporal 
									? 'bg-green-600 text-white hover:bg-green-700' 
									: 'bg-gray-300 text-gray-500 cursor-not-allowed'
								}`}
							>
								💾 Guardar
							</button>
							<button
								onClick={cancelarEdicion}
								className="px-3 py-1 bg-gray-500 text-white rounded text-xs hover:bg-gray-600"
							>
								❌ Cancelar
							</button>
						</div>
					</div>
				);
			} else if (campo === 'observaciones') {
				// Para editar observaciones (textarea)
				return (
					<textarea
						value={valorTemporal}
						onChange={(e) => setValorTemporal(e.target.value)}
						onBlur={guardarEdicion}
						onKeyDown={manejarTecla}
						className="border rounded px-2 py-1 text-sm min-w-[200px] resize-none"
						rows="3"
						placeholder="Ingresa observaciones..."
						autoFocus
					/>
				);
			} else {
				// Para editar texto/fechas (input normal)
				return (
					<input
						type={tipo}
						value={valorTemporal}
						onChange={(e) => setValorTemporal(e.target.value)}
						onBlur={guardarEdicion}
						onKeyDown={manejarTecla}
						className={`border rounded px-1 py-1 w-full text-center ${className}`}
						autoFocus
					/>
				);
			}
		}
		
		return (
			<span
				onDoubleClick={() => iniciarEdicion(certificado.id, campo, valor)}
				className="cursor-pointer hover:bg-gray-100 px-1 py-1 rounded transition-colors block"
				title="Doble clic para editar"
			>
				{campo === 'observaciones' && (!valor || valor === '—') ? (
					<span className="text-gray-400 italic">Sin observaciones</span>
				) : campo === 'observaciones' ? (
					<div className="max-w-[220px] overflow-hidden">
						<div className="truncate">{valor}</div>
						{valor && valor.length > 50 && (
							<div className="text-xs text-gray-500">...</div>
						)}
					</div>
				) : (
					valor
				)}
			</span>
		);
	};

	const certificadosFiltrados = useMemo(() => {
		let data = certificados.slice();
		if (filtro !== 'TODOS') data = data.filter(c => c.estatus === filtro);
		
		// 3. ACTUALIZAR LÓGICA DE BÚSQUEDA
		if (query.trim()) {
			const q = query.toLowerCase();
			data = data.filter(c => 
				c.marca?.toLowerCase().includes(q) ||
				c.modelo?.toLowerCase().includes(q) ||
				c.nom_identifier?.toLowerCase().includes(q) // <-- INCLUIR NOM_IDENTIFIER
			);
		}
		return data;
	}, [certificados, filtro, query]);

	const total = certificados.length;
	const vigentes = certificados.filter(c => c.estatus === 'VIGENTE').length;
	const porVencer = certificados.filter(c => c.estatus === 'POR VENCER').length;
	const vencidos = certificados.filter(c => c.estatus === 'VENCIDO').length;

	const pageCount = Math.max(1, Math.ceil(certificadosFiltrados.length / pageSize));
	const currentPage = Math.min(page, pageCount);
	const startIdx = (currentPage - 1) * pageSize;
	const pageItems = certificadosFiltrados.slice(startIdx, startIdx + pageSize);

	return (
		<div className="p-6 max-w-7xl mx-auto bg-gray-50 min-h-screen">
			{/* Header */}
			<div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8 bg-white rounded-lg shadow-sm border p-6">
				<div>
					<h2 className="text-3xl font-bold text-gray-900 mb-2">Certificados NOM</h2>
					<p className="text-gray-600 text-base">Administra y consulta los certificados NOM de tus productos</p>
					<p className="text-gray-400 text-sm mt-2">
						Última actualización: {ultimaActualizacion.toLocaleString('es-ES')}
					</p>
				</div>
				<div className="flex gap-3 mt-4 lg:mt-0">
					<button 
						onClick={actualizarListaCertificados}
						className="bg-white hover:bg-gray-50 text-gray-700 px-4 py-2.5 rounded-lg font-medium shadow-sm border border-gray-300 transition-colors duration-200 flex items-center gap-2"
						title="Actualizar cálculos ahora"
					>
						<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-gray-500">
							<path d="M17.65,6.35C16.2,4.9 14.21,4 12,4A8,8 0 0,0 4,12A1,1 0 0,0 5,13A1,1 0 0,0 6,12A6,6 0 0,1 12,6C13.66,6 15.14,6.69 16.22,7.78L13,11H20V4L17.65,6.35Z" />
						</svg>
						Actualizar
					</button>
					<button 
						onClick={() => setShowForm(!showForm)} 
						className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 rounded-lg font-medium shadow-sm transition-colors duration-200 flex items-center gap-2"
					>
						<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
							<path d="M19,13H13V19H11V13H5V11H11V5H13V11H19V13Z" />
						</svg>
						Nuevo Certificado
					</button>
				</div>
			</div>

			{/* Summary cards */}
			<div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
				<div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
					<div className="flex items-center justify-between">
						<div>
							<p className="text-sm font-medium text-gray-600">Total</p>
							<p className="text-2xl font-bold text-gray-900 mt-1">{total}</p>
						</div>
						<div className="bg-gray-100 p-3 rounded-lg">
							<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" className="text-gray-600">
								<path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
							</svg>
						</div>
					</div>
				</div>
				<div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
					<div className="flex items-center justify-between">
						<div>
							<p className="text-sm font-medium text-gray-600">Vigentes</p>
							<p className="text-2xl font-bold text-green-600 mt-1">{vigentes}</p>
						</div>
						<div className="bg-green-50 p-3 rounded-lg">
							<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" className="text-green-600">
								<path d="M9,20.42L2.79,14.21L5.62,11.38L9,14.77L18.88,4.88L21.71,7.71L9,20.42Z" />
							</svg>
						</div>
					</div>
				</div>
				<div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
					<div className="flex items-center justify-between">
						<div>
							<p className="text-sm font-medium text-gray-600">Por vencer</p>
							<p className="text-2xl font-bold text-amber-600 mt-1">{porVencer}</p>
						</div>
						<div className="bg-amber-50 p-3 rounded-lg">
							<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" className="text-amber-600">
								<path d="M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,6A1,1 0 0,1 13,7A1,1 0 0,1 12,8A1,1 0 0,1 11,7A1,1 0 0,1 12,6M14,15A1,1 0 0,1 13,16H11A1,1 0 0,1 10,15V9A1,1 0 0,1 11,8H13A1,1 0 0,1 14,9V15Z" />
							</svg>
						</div>
					</div>
				</div>
				<div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
					<div className="flex items-center justify-between">
						<div>
							<p className="text-sm font-medium text-gray-600">Vencidos</p>
							<p className="text-2xl font-bold text-red-600 mt-1">{vencidos}</p>
						</div>
						<div className="bg-red-50 p-3 rounded-lg">
							<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" className="text-red-600">
								<path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" />
							</svg>
						</div>
					</div>
				</div>
			</div>

			{/* Filtros */}
			<div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
				<div className="flex flex-col space-y-4">
					<div className="flex flex-wrap gap-2">
						{['TODOS', 'VIGENTE', 'POR VENCER', 'VENCIDO'].map(opt => (
							<button
								key={opt}
								className={`px-4 py-2 rounded-lg font-medium transition-colors duration-200 ${
									filtro === opt 
									? 'bg-blue-600 text-white shadow-sm' 
									: 'bg-gray-100 text-gray-700 hover:bg-gray-200'
								}`}
								onClick={() => { setFiltro(opt); setPage(1); }}
							>
								{opt === 'TODOS' ? 'Todos' : opt.charAt(0) + opt.slice(1).toLowerCase().replace('_', ' ')}
							</button>
						))}
					</div>
					<div className="flex flex-col lg:flex-row gap-4 lg:items-center lg:justify-between">
						<div className="relative flex-1 max-w-md">
							<svg className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
							</svg>
							<input
								type="text"
								placeholder="Buscar por marca, modelo o ID. NOM..." // CAMBIO EN PLACEHOLDER
								value={query}
								onChange={e => { setQuery(e.target.value); setPage(1); }}
								className="pl-10 pr-4 py-2.5 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-200"
							/>
						</div>
						<div className="flex gap-6 items-center">
						</div>
					</div>
				</div>
			</div>

			{/* Modal para agregar certificado */}
			{showForm && (
				<div className="fixed inset-0 z-50 overflow-y-auto">
					{/* Overlay */}
					<div 
						className="fixed inset-0 bg-black bg-opacity-40 transition-opacity duration-300"
						onClick={() => setShowForm(false)}
					></div>
					
					{/* Modal */}
					<div className="flex min-h-full items-center justify-center p-4">
						<div className="relative bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto transform transition-all duration-300 scale-100">
							{/* Header */}
							<div className="flex items-center justify-between p-6 border-b border-gray-200">
								<div>
									<h3 className="text-xl font-semibold text-gray-900">Nuevo Certificado NOM</h3>
									<p className="text-sm text-gray-600 mt-1">Complete la información para registrar un nuevo certificado</p>
								</div>
								<button 
									onClick={() => setShowForm(false)}
									className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
									title="Cerrar formulario"
								>
									<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
										<path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" />
									</svg>
								</button>
							</div>

							<form onSubmit={handleSubmit} className="p-6 space-y-6">
								{/* Información básica del producto */}
								<div className="space-y-4">
									<h4 className="text-base font-medium text-gray-900">Información del Producto</h4>
									<div className="grid grid-cols-1 md:grid-cols-3 gap-4"> {/* Ajustado a 3 columnas para NOM */}
										<div className="space-y-2">
											<label className="block text-sm font-medium text-gray-700">
												Marca
											</label>
											<input 
												name="marca" 
												value={form.marca} 
												onChange={handleChange} 
												className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-200"
												placeholder="Marca "
												required 
											/>
										</div>
										<div className="space-y-2">
											<label className="block text-sm font-medium text-gray-700">
												Modelo
											</label>
											<input 
												name="modelo" 
												value={form.modelo} 
												onChange={handleChange} 
												className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-200"
												placeholder="Modelo "
												required 
											/>
										</div>
										{/* 4. NUEVO CAMPO NOM_IDENTIFIER EN EL FORMULARIO */}
										<div className="space-y-2">
											<label className="block text-sm font-medium text-gray-700">
												Identificador NOM
											</label>
											<input 
												name="nom_identifier" 
												value={form.nom_identifier} 
												onChange={handleChange} 
												className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-200"
												placeholder="Ej: NOM-OS-1655666"
												required 
											/>
										</div>
									</div>
								</div>

								{/* Fechas del certificado */}
								<div className="space-y-4">
									<h4 className="text-base font-medium text-gray-900">Fechas de Vigencia</h4>
									<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
										<div className="space-y-2">
											<label className="block text-sm font-medium text-gray-700">
												Fecha de Emisión
											</label>
											<input 
												type="date" 
												name="creada" 
												value={form.creada} 
												onChange={handleChange} 
												className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-200"
												required 
											/>
										</div>
										<div className="space-y-2">
											<label className="block text-sm font-medium text-gray-700">
												Fecha de Vencimiento
											</label>
											<input 
												type="date" 
												name="vence" 
												value={form.vence} 
												onChange={handleChange} 
												className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-200"
												required 
											/>
										</div>
									</div>
								</div>

								{/* Documento y observaciones */}
								<div className="space-y-4">
									<h4 className="text-base font-medium text-gray-900">Documentación</h4>
									<div className="space-y-4">
										<div className="space-y-2">
											<label className="block text-sm font-medium text-gray-700">
												Certificado NOM (PDF) *
											</label>
											<input 
												type="file" 
												name="archivo" 
												accept="application/pdf" 
												onChange={handleChange} 
												className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-200 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
												required 
											/>
											<p className="text-xs text-gray-500">
												Solo archivos PDF. Tamaño máximo: 10MB
											</p>
										</div>
										<div className="space-y-2">
											<label className="block text-sm font-medium text-gray-700">
												Observaciones
												<span className="text-xs font-normal text-gray-500 ml-1">(Opcional)</span>
											</label>
											<textarea 
												name="observaciones" 
												value={form.observaciones} 
												onChange={handleChange} 
												className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-200 resize-none"
												rows="3"
												placeholder="Notas adicionales, comentarios especiales, etc..."
											></textarea>
										</div>
									</div>
								</div>

								{/* Botones de acción */}
								<div className="flex flex-col sm:flex-row gap-3 justify-end pt-6 border-t border-gray-200">
									<button 
										type="button"
										onClick={() => setShowForm(false)}
										className="px-4 py-2.5 bg-white border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors duration-200"
									>
										Cancelar
									</button>
									<button 
										type="submit" 
										className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors duration-200"
									>
										Agregar Certificado
									</button>
								</div>
							</form>
						</div>
					</div>
				</div>
			)}

			<div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
				<div className="overflow-x-auto">
					<table className="min-w-full divide-y divide-gray-200">
						<thead className="bg-gray-50">
							<tr>
								<th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Marca</th>
								<th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Modelo</th>
								{/* 4. NUEVA COLUMNA EN LA TABLA */}
								<th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID. NOM</th> 
								<th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha Creada</th>
								<th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha Vence</th>
								<th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Días restantes</th>
								<th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
								<th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">PDF</th>
								<th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Observaciones</th>
								<th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
							</tr>
						</thead>
						<tbody className="bg-white divide-y divide-gray-200">
							{pageItems.map((c, i) => (
								<tr key={i} className="hover:bg-gray-50 transition-colors duration-150">
									<td className="px-4 py-3 whitespace-nowrap">
										<CeldaEditable 
											certificado={c} 
											campo="marca" 
											valor={c.marca}
											className="font-medium text-gray-900"
										/>
									</td>
									<td className="px-4 py-3 whitespace-nowrap">
										<CeldaEditable 
											certificado={c} 
											campo="modelo" 
											valor={c.modelo}
											className="text-gray-900"
										/>
									</td>
									{/* 5. RENDERIZAR EL CAMPO NOM_IDENTIFIER (EDITABLE) */}
									<td className="px-4 py-3 whitespace-nowrap">
										<CeldaEditable 
											certificado={c} 
											campo="nom_identifier" 
											valor={c.nom_identifier || '—'}
											className="text-gray-700 font-medium"
										/>
									</td>
									<td className="px-4 py-3 whitespace-nowrap">
										<CeldaEditable 
											certificado={c} 
											campo="creada" 
											valor={c.creada}
											tipo="date"
											className="text-gray-600 text-sm"
										/>
									</td>
									<td className="px-4 py-3 whitespace-nowrap">
										<CeldaEditable 
											certificado={c} 
											campo="vence" 
											valor={c.vence}
											tipo="date"
											className="text-gray-600 text-sm"
										/>
									</td>
									<td className="px-4 py-3 whitespace-nowrap text-center">
										<span className={`font-mono text-sm font-medium ${
											c.dias < 0 ? 'text-red-600' : c.dias < 30 ? 'text-amber-600' : 'text-green-600'
										}`}>
											{c.dias}
										</span>
									</td>
									<td className="px-4 py-3 whitespace-nowrap text-center">
										<span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
											c.estatus === 'VIGENTE' ? 'bg-green-100 text-green-800' :
											c.estatus === 'POR VENCER' ? 'bg-amber-100 text-amber-800' :
											'bg-red-100 text-red-800'
										}`}>
											{c.estatus}
										</span>
									</td>
									<td className="px-4 py-3 whitespace-nowrap text-center">
										{c.pdf ? (
											<div className="flex gap-1 justify-center items-center">
												{editandoCelda?.id === c.id && editandoCelda?.campo === 'archivo' ? (
													<CeldaEditable 
														certificado={c} 
														campo="archivo" 
														valor={c.archivo} 
													/>
												) : (
													<>
														<button
															className="text-blue-600 hover:text-blue-800 transition-colors p-1.5 rounded hover:bg-blue-50"
															onClick={() => window.open(getPdfUrl(c.archivo), '_blank')}
															title="Ver PDF"
														>
															<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
																<path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
															</svg>
														</button>
														
														<button
															className="text-green-600 hover:text-green-800 transition-colors p-1.5 rounded hover:bg-green-50"
															onClick={async (event) => {
																try {
																	const button = event.target.closest('button');
																	const originalHTML = button.innerHTML;
																	button.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 4V2A10 10 0 0 0 2 12h2a8 8 0 0 1 8-8z"><animateTransform attributeName="transform" attributeType="XML" type="rotate" from="0 12 12" to="360 12 12" dur="1s" repeatCount="indefinite"/></path></svg>';
																	button.disabled = true;
																	
																	await descargarCertificadoPDF(c.archivo, c.marca, c.modelo);
																	
																	button.innerHTML = originalHTML;
																	button.disabled = false;
																	
																	button.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="green"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>';
																	setTimeout(() => {
																		button.innerHTML = originalHTML;
																	}, 1500);
																} catch (error) {
																	const button = event.target.closest('button');
																	button.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/></svg>';
																	button.disabled = false;
																	alert('Error al descargar el PDF: ' + error.message);
																}
															}}
															title="Descargar PDF"
														>
															<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
																<path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
															</svg>
														</button>
														
														<button
															className="text-gray-600 hover:text-gray-800 transition-colors p-1.5 rounded hover:bg-gray-50"
															onClick={() => iniciarEdicion(c.id, 'archivo', c.archivo)}
															title="Cambiar archivo PDF"
														>
															<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
																<path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
															</svg>
														</button>
													</>
												)}
											</div>
										) : <span className="text-gray-400 text-sm">—</span>}
									</td>
									<td className="px-4 py-3">
										<div className="max-w-xs">
											<CeldaEditable 
												certificado={c} 
												campo="observaciones" 
												valor={c.observaciones || '—'}
												className="text-gray-600 text-sm"
											/>
										</div>
									</td>
									<td className="px-4 py-3 text-center">
										<div className="flex items-center justify-center">
											<button
												onClick={() => abrirModalEliminar(c.id, c.marca, c.modelo)}
												className="text-red-600 hover:text-red-800 hover:bg-red-50 p-2 rounded-lg transition-colors duration-200 border border-transparent hover:border-red-200"
												title="Eliminar certificado"
											>
												<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
													<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
												</svg>
											</button>
										</div>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</div>

			{/* Paginación */}
			<div className="flex flex-col sm:flex-row items-center justify-between mt-6 gap-4 bg-white rounded-lg border border-gray-200 px-6 py-4">
				<div className="text-sm text-gray-700">
					Mostrando <span className="font-medium">{certificadosFiltrados.length === 0 ? 0 : startIdx + 1}</span> a <span className="font-medium">{Math.min(startIdx + pageSize, certificadosFiltrados.length)}</span> de <span className="font-medium">{certificadosFiltrados.length}</span> resultados
				</div>
				<div className="flex items-center gap-2">
					<button
						className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
						onClick={() => setPage(p => Math.max(1, p - 1))}
						disabled={currentPage === 1}
					>
						Anterior
					</button>
					<span className="text-sm text-gray-700 px-3 py-2">
						Página <span className="font-medium">{currentPage}</span> de <span className="font-medium">{pageCount}</span>
					</span>
					<button
						className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
						onClick={() => setPage(p => Math.min(pageCount, p + 1))}
						disabled={currentPage === pageCount}
					>
						Siguiente
					</button>
				</div>
			</div>

			{/* Información adicional */}
			<div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
				<div className="flex items-start gap-3">
					<svg className="flex-shrink-0 w-5 h-5 text-blue-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
						<path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
					</svg>
					<div className="text-sm text-blue-800">
						<p className="font-medium mb-1">Información útil:</p>
						<ul className="space-y-1 text-blue-700">
							<li>• Haz <strong>doble clic</strong> en cualquier celda (Marca, Modelo, ID. NOM, Fechas u Observaciones) para editarla directamente</li>
							<li>• Los certificados con menos de 30 días de vigencia aparecen marcados como "Por vencer"</li>
							<li>• Puedes filtrar los certificados por estado o buscar por marca/modelo/ID. NOM usando la barra de búsqueda</li>
						</ul>
					</div>
				</div>
			</div>

			{/* Modal de confirmación para eliminar */}
			{showDeleteModal && (
				<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
					<div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
						<div className="p-6">
							<div className="flex items-center gap-3 mb-4">
								<div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
									<svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
									</svg>
								</div>
								<div>
									<h3 className="text-lg font-semibold text-gray-900">Confirmar eliminación</h3>
									<p className="text-sm text-gray-600">Esta acción no se puede deshacer</p>
								</div>
							</div>

							{certificadoAEliminar && (
								<div className="mb-4 p-3 bg-gray-50 rounded-lg border">
									<p className="text-sm text-gray-700">
										¿Estás seguro de que deseas eliminar el certificado de:
									</p>
									<p className="font-semibold text-gray-900 mt-1">
										{certificadoAEliminar.marca} {certificadoAEliminar.modelo}
									</p>
								</div>
							)}

							<div className="mb-6">
								<label htmlFor="password-confirmation" className="block text-sm font-medium text-gray-700 mb-2">
									Ingresa tu contraseña para confirmar:
								</label>
								<input
									id="password-confirmation"
									type="password"
									value={passwordConfirmacion}
									onChange={(e) => setPasswordConfirmacion(e.target.value)}
									className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
									placeholder="Tu contraseña"
									disabled={loadingDelete}
									onKeyDown={(e) => {
										if (e.key === 'Enter') {
											confirmarEliminacion();
										}
									}}
								/>
							</div>

							<div className="flex items-center justify-end gap-3">
								<button
									onClick={cerrarModalEliminar}
									className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200"
									disabled={loadingDelete}
								>
									Cancelar
								</button>
								<button
									onClick={confirmarEliminacion}
									disabled={loadingDelete || !passwordConfirmacion.trim()}
									className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 flex items-center gap-2"
								>
									{loadingDelete ? (
										<>
											<svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
												<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
												<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
											</svg>
											Eliminando...
										</>
									) : (
										'Eliminar'
									)}
								</button>
							</div>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}

export default Certificado;
